import React, { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState, Suspense } from "react";
import { useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import classNames from "classnames";

import {
	ChannelIconStatus,
	CustomPage,
	ENavbarTypes,
	EPreviewTypes,
	EventNavbar,
	FeatureFlagsEnum,
	IHomepageMainNavItems,
	ModuleMap,
	NavMainItems,
	PageModule,
	PageModuleType,
	Session,
	SessionTypesEnum,
	SessionTypesFeatures,
} from "../../../../../types/working-model";
import Icon, { COLORS, ICONS } from "../../../../general-ui/icon";
import MainEventBanner from "../../../../live-event/modules/main-event-banner/main-event-banner";
import About from "../../../../live-event/modules/about/about";
import Speakers from "../../../../live-event/modules/speakers/speakers";
import CallOut from "../../../../live-event/modules/call-out/call-out";
import Sponsors from "../../../../live-event/modules/sponsors/sponsors";
import Products from "../../../../live-event/modules/products/products";
import Videos from "../../../../live-event/modules/video/videos";
import Accordion from "../../../../live-event/modules/accordion/accordion";
import BlankSection from '../../homepage/editor/blank-section';
import { EditorSizes, TemplateClassNames, TemplateNames } from '../../../../../types/template-layouts';
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import NewDropdown from "../../../../general-ui/dropdown/new-dropdown";
import { EIcon } from "../../../../general-ui/icon/icon";
import { ParamsProps } from "../../../../live-event/live-event";
import HomePreviewModal from "../editor/preview/home-preview";
import useTranslationModules, { useTranslate } from "../../../../../i18n/useTranslationModules";
import { resetScrollState } from "../../../../../store/actions/admin/event-panel";
import Controls, { InitialControlsDisplay } from "../../session/editor/moduleControls/controls";
import { getHomepageDefaultLanguage } from '../../../../../utils/get-language';
import { GetDefaultEventNavbar } from "../../../../../store/utils/create-event";
import EditSectionColorsModal, { EContentColorOverrideNames } from "../../../../general-ui/edit-section-colors/edit-section-colors-modal";
import { updateEventNavbar } from "../../../../../store/actions/admin/create-event";
import { getEventNavbarStyling } from "../../../../live-event/utils";
import ProfileDetailsDropdown from "../../../../live-event/marketing-page/navigation/profile-details-dropdown";
import Footer from "../../../../live-event/marketing-page/footer/footer";
import './main-preview.scss';
import MainEventBannerLive from "../../../../live-event/modules/main-event-banner-live/main-event-banner-live";
import { PATHNAMES } from "../../../../../utils/admin-routing-utils";
import { getMockBlProfile, getTemplateClassName } from "../../../../../utils/utils";
import ChannelCustomizedIconModal from "./channel-custom-icon";
import { iconNameList } from "../../../../../utils/utils";
import images from '../../../../../images';
import { getMainNavIcon } from "../../../../live-event/marketing-page/navigation/marketing-nav";
import { mainNavIcons } from ".";
import { OptionalComponent } from "../../../../../utils/optional-component";
import { useIsAdminCreateSessionPage } from '../../../../../hooks/path.hooks';
import { useNavigationVisibility } from '../../../../live-event/marketing-page/navigation-v2/hooks/navigation.hooks';
import NavigationToggle from "../../../../live-event/marketing-page/navigation-v2/navigation-toggle";
import { useGetNavIconName } from "../../../../live-event/marketing-page/navigation-v2/hooks/navigation-icon.hooks";
import { useIsSingleSessionWithoutHome } from "../../../../../hooks/session.hooks";
import { setIsNavigationVisible } from "../../../../../store/actions/admin/content-actions";
import { useIsNewNavigation } from "../../../../../hooks/navigation.hooks";
import EditEventHeaderModal from "../../session/editor/edit-event-header-modal/edit-event-header-modal";

const Agenda = React.lazy(() => import("../../../../live-event/modules/agenda/agenda"));

// used to convert the SessionTypesEnum into the tabs used in the editor url
const editorSessionPageUrlTabs: { [key: string]: string } = {
	'onDemand': 'on-demand',
	'broadcast': 'event-agenda'
};

interface IMainPreviewProps {
	eventMainNavItems: IHomepageMainNavItems[];
}

interface IRenderLiveModulesProps {
	modules: PageModule[];
	template: string;
}

export const RenderLiveModules = React.forwardRef<HTMLDivElement, IRenderLiveModulesProps>(({ modules, template }, previewContainerRef: React.ForwardedRef<HTMLDivElement>) => {
	const sessions = useTypedSelector(state => state.CreateEventReducer.workingEvent?.sessions);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const editorViewSize = useTypedSelector(state => state.CreateEventReducer.editorSize);
	const scrollToModule = useTypedSelector(state => state.EventPanelReducer.scrollToModule);
	const displayLiveBanner = useTypedSelector(state => state.CreateEventReducer.displayLiveBanner);

	const dispatch = useDispatch();
	const moduleRefMap = useRef<any>({});

	const handleScroll = useCallback(async (type: PageModuleType) => {
		const moduleRef = moduleRefMap.current[type];
		if (previewContainerRef && moduleRef) {
			(previewContainerRef as MutableRefObject<HTMLDivElement>).current.scrollTop = moduleRef.offsetTop;
		}
	}, [previewContainerRef]);

	useEffect(() => {
		if (scrollToModule !== null) {
			handleScroll(scrollToModule);
		}
		// after triggering the scroll, reset state incase
		// user clicks same module after scrolling
		setTimeout(() => dispatch(resetScrollState()), 2000);
	}, [dispatch, scrollToModule, handleScroll]);

	const renderedModules = useMemo(() => {
		return modules.map(module => {
			let moduleTitle = ModuleMap[module.type];
			if (module.content?.custom_heading?.changed === 'true') {
				moduleTitle = module.content?.custom_heading?.base || '';
			}

			if (!module.is_on || !template) return null;
			switch (module.type) {
				case PageModuleType.main_event_banner: {
					if (displayLiveBanner && (module.content?.enable_live_banner ?? true)) {
						const timestampedSessions = workingEvent?.sessions.filter(session => SessionTypesFeatures[session.session_type].has_start_time);
						return (
							<MainEventBannerLive
								module={module}
								template={getTemplateClassName(workingEvent?.template.name)}
								key={module.id}
								liveSessionList={timestampedSessions ?? []}
							/>
						);
					}
					return <MainEventBanner eventBundle={workingEvent} key={module.id} module={module} template={template} ref={(ref: any) => moduleRefMap.current[module.type] = ref} />;
				}
				case PageModuleType.about: return <About key={module.id} module={module} template={template} ref={(ref: any) => moduleRefMap.current[module.type] = ref} />;
				case PageModuleType.speakers: return <Speakers key={module.id} module={module} template={template} ref={(ref: any) => moduleRefMap.current[module.type] = ref} />;
				case PageModuleType.agenda: return <Suspense key={module.id} fallback=""><Agenda isLive={false} module={module} template={template} sessions={sessions || []} eventBundle={workingEvent} ref={(ref: any) => moduleRefMap.current[module.type] = ref} editorViewSize={editorViewSize} editable={false} /></Suspense>;
				case PageModuleType.callout: return <CallOut key={module.id} module={module} template={template} ref={(ref: any) => moduleRefMap.current[module.type] = ref} />;
				case PageModuleType.sponsors: return <Sponsors key={module.id} module={module} template={template} ref={(ref: any) => moduleRefMap.current[module.type] = ref} />;
				case PageModuleType.products: return <Products key={module.id} module={module} template={template} ref={(ref: any) => moduleRefMap.current[module.type] = ref} />;
				case PageModuleType.videos: return <Videos key={module.id} module={module} template={template} ref={(ref: any) => moduleRefMap.current[module.type] = ref} />;
				case PageModuleType.blank: return <BlankSection key={module.id} page_module={module} ref={(ref: any) => moduleRefMap.current[module.type] = ref} />;
				case PageModuleType.accordion: return <Accordion key={module.id} module={module} template={template} ref={(ref: any) => moduleRefMap.current[module.type] = ref} />;
				default:
					return <div key={module.id}>{moduleTitle}</div>;
			}
		});
	}, [displayLiveBanner, editorViewSize, modules, sessions, template, workingEvent]);

	if (!modules?.length) return null;
	return (
		<div>
			{renderedModules}
		</div>
	);
});

RenderLiveModules.displayName = "RenderLiveModules";

const MainPreview: React.FC<IMainPreviewProps> = ({
	eventMainNavItems,
}) => {
	const logo = useTypedSelector(state => state.CreateEventReducer?.workingEvent?.homepage?.header?.logo);
	const post_register_home_modules = useTypedSelector(state => state.CreateEventReducer.workingEvent?.homepage?.post_register_home_modules);
	const _template = useTypedSelector(state => state.CreateEventReducer.workingEvent?.template.name);
	const customPages = useTypedSelector(state => state.CreateEventReducer.workingEvent?.custom_pages);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const { language } = useParams<ParamsProps>();
	const baseLanguage = getHomepageDefaultLanguage();

	useTranslationModules({ language: language || baseLanguage });

	const templateName = _template ?? TemplateNames.Apollo;
	const template = getTemplateClassName(templateName);

	const navPath = workingEvent ?
		PATHNAMES.Event?.[NavMainItems.Home]?.(workingEvent.uuid, (language ?? workingEvent?.sessions[0].default_language)) : '';

	return (
		<>
			<PostRegisterSideBar
				disableProfileBubble
				{...{ eventMainNavItems, template, logo }}
				activeMenuItem={NavMainItems.Home}
				customPages={customPages}
			/>
			<Link to={navPath} className="post-register-main-body post-register-homepage-link">
				{post_register_home_modules && (
					<RenderLiveModules modules={post_register_home_modules} template={template} />
				)}
				{workingEvent?.homepage?.footer ? (
					<Footer footer={workingEvent.homepage.footer} template={template} />
				) : null}
			</Link>
		</>
	);
};

export default MainPreview;

interface MainNavItemProps {
	navPath: string;
	navItemColor: string;
	active: boolean;
	isDesktop: boolean;
	icon: string | null;
	iconElement?: JSX.Element | undefined;
	name: string;
	navItemActiveAccent: string;
	item?: IHomepageMainNavItems;
}

const MainNavItem: React.FC<MainNavItemProps> = ({ navPath, navItemColor, active, isDesktop, icon, name, navItemActiveAccent, item, iconElement }) => {
	const [displayUploadImageModal, setDisplayUploadImageModal] = useState(false);
	const [customIconUrl, setCustomIconUrl] = useState("");
	const [isCustomIcon, setIsCustomIcon] = useState(item?.is_custom);
	const [customIconForCustomPage, setCustomIconForCustomPage] = useState("");
	const showNewNav = useIsNewNavigation();
	const defaultIconNameOrCustomIconUuid = icon;

	//Get the list of icons from the channel level.
	const iconsChannel = useTypedSelector(state => state.CreateEventReducer.channelIcons);

	//Custom icons for main nav item (Home and Agenda)
	useEffect(() => {
		if (iconsChannel && iconsChannel.length > 0) {
			if (isCustomIcon) {
				const icon = iconsChannel?.find(icon => (icon?.uuid === item?.custom_icon_uuid) && (icon.status !== ChannelIconStatus.deleted));
				if (icon) {
					setCustomIconUrl(icon.url);
					return;
				}
			}
		}
		setCustomIconUrl("");
	}, [isCustomIcon, iconsChannel, item?.custom_icon_uuid, icon]);

	//Custom icons for custom pages
	useEffect(() => {
		/* If the icon is the default icon, defaultIconNameOrCustomIconUuid will be included in the iconNameList (its name can be like breakoutRooms). If it is a custom, the defaultIconNameOrCustomIconUuid is UUID. I used the UUID since we can upload multiple images with the same name. The iconNameList does not contain the custom UUID. So we know it is a custom one and retrieve the URL. */
		if (defaultIconNameOrCustomIconUuid && !iconNameList().includes(defaultIconNameOrCustomIconUuid)) {
			const iconForCustomPage = iconsChannel?.find(i => (i?.uuid === defaultIconNameOrCustomIconUuid) && (i.status !== ChannelIconStatus.deleted));
			if (iconForCustomPage) {
				setCustomIconForCustomPage(iconForCustomPage.url);
			}
			else {
				setCustomIconForCustomPage("");
			}
		}
	}, [defaultIconNameOrCustomIconUuid, icon, iconsChannel]);

	//make sure the button to upload or select the channel icons only pops up on the main nav items.
	const nameNavArray = ['Home', 'Session', 'Sessions', 'Leaderboard'];
	const isMainNavItem = Boolean(item?.name && nameNavArray.includes(item?.name));

	const openChannelIconsModal = () => {
		setDisplayUploadImageModal(true);
	};

	const renderedIcon = useMemo(() => {
		if (isDesktop) {
			if (customIconUrl || customIconForCustomPage) {
				return <img className={classNames("custom-icon")} src={customIconUrl || customIconForCustomPage} />;
			}

			if (iconElement) return iconElement;

			if (icon) {
				return <Icon name={(iconNameList().includes(icon) || isMainNavItem) ? icon : iconNameList()[0]} color={COLORS.WHITE} size={24} />;
			}
		}
	}, [customIconForCustomPage, customIconUrl, icon, iconElement, isDesktop, isMainNavItem]);

	return (
		<li className="side-nav-link">
			{isMainNavItem && (
				<button
					className="round edit-button edit-image-button"
					onClick={openChannelIconsModal}
				>
					<Icon name={ICONS.IMAGE} size={20} color={COLORS.WHITE} />
				</button>
			)}

			{displayUploadImageModal && isMainNavItem && <ChannelCustomizedIconModal
				displayUploadImageModal={displayUploadImageModal}
				setDisplayUploadImageModal={setDisplayUploadImageModal}
				itemUuid={item?.uuid}
				setIsCustomIcon={setIsCustomIcon}
			/>}
			<Link to={navPath} className={classNames(navItemColor, { active: active })}>
				{renderedIcon}
				<h6>{name}</h6>
			</Link>
			{isDesktop && !showNewNav && <div className={classNames("active-border", active && navItemActiveAccent)} />}
		</li>
	);
};

interface IPostRegisterSideBarProps {
	eventMainNavItems: IHomepageMainNavItems[];
	template: string;
	activeMenuItem: string;
	logo?: string | null;
	disableProfileBubble?: boolean;
	customPages: CustomPage[] | undefined
	isSingleSession?: boolean;
}

export const PostRegisterSideBar: React.FC<IPostRegisterSideBarProps> = (props) => {
	// activeMenuItem here is now a prop rather than a redux state
	const {
		eventMainNavItems,
		template,
		activeMenuItem,
		logo,
		disableProfileBubble = false,
		isSingleSession = false,
		customPages
	} = props;
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const editorSize = useTypedSelector(state => state.CreateEventReducer.editorSize);
	const user = useTypedSelector(state => state.AuthReducer.user);
	const previewMode = useTypedSelector(state => state.CreateEventReducer.previewMode);
	const featureFlags = useTypedSelector(state => state.FeatureFlagsReducer.featureFlags);
	const isNavigationOpen = useTypedSelector(state => state.ContentReducer.isNavigationOpen);

	const [openTabletMenu, setOpenTabletMenu] = useState(false);
	const [openEditNavItemColor, setOpenEditNavItemColor] = useState<boolean>(false);
	const [openEditModal, setOpenEditModal] = useState<boolean>(false);

	const isSessionPage = useIsAdminCreateSessionPage();
	const isNewNavigation = useIsNewNavigation();
	const isSingleSessionWithoutHomePage = useIsSingleSessionWithoutHome();

	const navbarType = workingEvent?.settings?.event_navbar?.navbarType;
	const navIconName = useGetNavIconName({ navbarType });

	const isSticky = workingEvent?.settings?.event_navbar?.isSticky;
	const isTopNavbar = navbarType === ENavbarTypes.Horizontal;
	
	const isDesktop = editorSize === EditorSizes.desktop;
	const shouldShowNewNav = isNewNavigation && isDesktop;
	const isSessionPageNewNav = isNewNavigation && isDesktop && (isSessionPage || isSingleSession);

	const { isNavigationVisible, handleClick } = useNavigationVisibility({ shouldShowControls: isSessionPageNewNav, className: 'post-register-side-bar' });

	const directoryIsOn = !!workingEvent?.social_settings?.directory?.isOn;

	const leaderboardFeatureFlagOn = featureFlags[FeatureFlagsEnum.leaderboard];

	const { language }: ParamsProps = useParams();
	const baseLanguage = getHomepageDefaultLanguage();
	const { t } = useTranslate(['homepage', 'home']);

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(setIsNavigationVisible(false));
	}, [dispatch]);

	function renderNavItems(isDesktop: boolean) {
		// get session page tab.
		let sessionTab: string | undefined;
		const eventSessionTypes = new Set<SessionTypesEnum>();
		workingEvent?.sessions.forEach((session: Session) => eventSessionTypes.add(session.session_type));
		// If broadcast session exists, go there. Otherwise, use order of the SessionTypesEnum
		if (eventSessionTypes.has(SessionTypesEnum.broadcast)) {
			sessionTab = editorSessionPageUrlTabs.broadcast;
		} else {
			for (const sessionType in SessionTypesEnum) {
				if (eventSessionTypes.has(SessionTypesEnum[sessionType as keyof typeof SessionTypesEnum])) {
					sessionTab = editorSessionPageUrlTabs[sessionType];
					break;
				}
			}
		}

		const mainNavItems = eventMainNavItems
			.filter(navItem => {
				if (!leaderboardFeatureFlagOn && navItem.name === NavMainItems.Leaderboard) {
					return false;
				}
				return navItem.name.includes('Session')
					? workingEvent?.settings.display_sessions ?? true
					: true;
			})
			.map(item => {
				const icon = mainNavIcons(item.name);
				const iconEl = getMainNavIcon(item.name);
				const pathLanguage = language ?? workingEvent?.sessions[0].default_language;
				// if going to /home we don't need another param, if going to /sessions use tab
				const pathParam = item.name === NavMainItems.Home ?
					undefined :
					(sessionTab ?? '');

				const navPath = workingEvent
					? item.name !== NavMainItems.Session
						? PATHNAMES.Event?.[item.name]?.(workingEvent.uuid, pathLanguage, pathParam)
						: PATHNAMES.Session.SessionLink(workingEvent.uuid, workingEvent.sessions[0].uuid, workingEvent.sessions[0].default_language)
					: '/';

				const isSingleSession = workingEvent?.sessions.length === 1;
				const active = activeMenuItem
					// if activeMenuItem isn't an empty string,
					// check which tab is active
					? activeMenuItem.toUpperCase() === item.name.toUpperCase()
					// if isSingleSession and active menu is empty,
					// session(s) tab should be active
					: isSingleSession ? (item.name === NavMainItems.Session) : item.name === NavMainItems.Home;

				const name = item.custom_name[language] && item.custom_name.changed
					? t(`home:nav.${item.custom_name.base}`, item.custom_name[language] as string)
					: item.custom_name.base;

				if (!item.is_on) return null;
				return (
					<MainNavItem
						key={item.custom_name.base}
						name={name}
						navPath={navPath}
						navItemColor={navItemColor}
						active={active}
						isDesktop={isDesktop}
						icon={icon}
						iconElement={iconEl}
						navItemActiveAccent={navItemActiveAccent}
						item={item}
					/>
				);
			});

		const customNavItems = customPages
			? customPages
				.filter(page => page.is_on && !page.deleted)
				.map(page => {
					return (
						<MainNavItem
							key={page.uuid}
							name={t(`home:nav.customPage.${page.path}`, page.page_name[language] as string ?? page.page_name.base)}
							navPath={workingEvent ? PATHNAMES.Event.CustomPageLink(workingEvent.uuid, page.path, language || getHomepageDefaultLanguage()) : '/'}
							navItemColor={navItemColor}
							active={activeMenuItem === page.path}
							isDesktop={isDesktop}
							icon={page.icon}
							navItemActiveAccent={navItemActiveAccent}
						/>
					);
				})
			: [];

		const renderDirectoryNavItem = directoryIsOn ? (
			<MainNavItem
				key="directory"
				navPath={workingEvent ? PATHNAMES.Event.DirectoryLink(workingEvent.uuid, language || getHomepageDefaultLanguage()) : '/'}
				icon={ICONS.PEOPLE_NAV}
				iconElement={getMainNavIcon(NavMainItems.People)}
				name={t("directory.Directory")}
				navItemActiveAccent={navItemActiveAccent}
				active={activeMenuItem === NavMainItems.Social}
				navItemColor={navItemColor}
				isDesktop={isDesktop}
			/>
		) : null;

		return [
			...mainNavItems,
			renderDirectoryNavItem,
			...customNavItems
		];
	}

	// admin customization of event navbar
	const eventNavbar: EventNavbar = workingEvent?.settings.event_navbar || GetDefaultEventNavbar();
	const { backgroundColor, backgroundImg, navItemColor, navItemActiveAccent } = getEventNavbarStyling(eventNavbar.styling_overrides);
	const displaySideBarControls: InitialControlsDisplay = { editColors: true, editLayout: isNewNavigation };

	const handleNavItemColorOverrides = (colorKey: string) => {
		if (!workingEvent || !eventNavbar.styling_overrides) return;

		let workingContentOverrides = { ...eventNavbar.styling_overrides.content };
		// If color key is only -color, then we are restoring to the default color.
		const updateColor = colorKey === "-color" ? "" : colorKey;

		workingContentOverrides ? workingContentOverrides.navItems = updateColor : workingContentOverrides = { navItems: updateColor };

		dispatch(updateEventNavbar({
			...eventNavbar,
			styling_overrides: {
				...eventNavbar.styling_overrides,
				content: workingContentOverrides
			}
		}));
	};

	return (
		<>
			<NavigationToggle isDesktop={isSessionPageNewNav} />

			<div
				className={classNames(
					"editor-wrapper post-register-side-bar",
					template,
					editorSize,
					backgroundColor,
					{
						sessions: activeMenuItem === NavMainItems.Sessions,
						'single-session': isSingleSession,
						'show-navigation': isSessionPageNewNav ? isNavigationVisible : true,
						'navigation-fixed': isSessionPageNewNav ? isNavigationOpen : true,
						'navigation-toggle-active': isSessionPageNewNav || true,
						'new-navigation': shouldShowNewNav,
						'not-session-page': isNewNavigation && !isSessionPageNewNav,
						'new-top-navigation': isTopNavbar && shouldShowNewNav,
						'sticky-top-navigation': isSticky && shouldShowNewNav,
						'single-session-nav': isSingleSessionWithoutHomePage,
					})
				}
			>
				{eventNavbar && <Controls
					display={displaySideBarControls}
					module={eventNavbar}
					openEditModal={() => setOpenEditModal(true)}
					baseLanguage={baseLanguage}
					language={language}
				/>}
				<div
					className={classNames(
						'sidebar-container',
						{ 'new-top-navigation': isTopNavbar && isNewNavigation }
					)}
					style={backgroundImg}
				>
					<OptionalComponent display={isSessionPageNewNav}>
						<button className={classNames('navigation-fixed-button no-style no-padding')} onClick={handleClick}>
							{isNavigationOpen ? (
								<Icon name={navIconName} color={COLORS.BLACK} size={20} />
							) : (
								<EIcon name='menu' size='large' className='icon-menu' />
							)}
						</button>
					</OptionalComponent>

					<div className="post-register-preview-logo">
						{logo ? (
							<img
								className="logo"
								alt="Logo"
								src={logo}
							/>
						) : (
							<img
								className="logo"
								alt="Logo"
								src={images.HeaderImage}
							/>
						)}
					</div>
					{isDesktop && (
						<>
							<ul className={classNames(
								'main-nav-container read-only-exception',
								{ 'new-top-navigation': isTopNavbar && isNewNavigation },
							)}>
								{renderNavItems(isDesktop)}
								<button onClick={() => setOpenEditNavItemColor(true)} className="round edit-button">
									<Icon name={ICONS.EDIT} color={COLORS.WHITE} size={12} />
								</button>
							</ul>
							<aside className="profile-details-actions" style={disableProfileBubble ? { pointerEvents: "none" } : {}}>
								{(user && workingEvent) ? (
									<ProfileDetailsDropdown
										profile={getMockBlProfile(user)}
										channel={user.active_channel}
										eventBundle={workingEvent}
										template={template || TemplateClassNames.Apollo}
										isEditor
									/>
								) : null}
							</aside>
						</>
					)
					}
					{
						!isDesktop && (
							<div className="mobile-header-actions-container">
								<div className={classNames("dropdown-background", { open: openTabletMenu })} />
								<NewDropdown
									className={classNames("menu-tablet", navItemColor)}
									popupClassName={classNames("menu-tablet-popup", backgroundColor)}
									showArrow={false}
									renderTitle={({ isOpen }) => {
										setOpenTabletMenu(isOpen);
										return isOpen ? (
											<EIcon name="close" size="large" />
										) : (
											<EIcon name="menu" size="large" />
										);
									}}
								>
									<div className="main-nav-container">
										{renderNavItems(isDesktop)}
									</div>
								</NewDropdown>
								{(user && workingEvent) ? (
									<aside className="profile-details-actions" style={disableProfileBubble ? { pointerEvents: "none" } : {}}>
										<ProfileDetailsDropdown
											profile={getMockBlProfile(user)}
											channel={user.active_channel}
											eventBundle={workingEvent}
											template={template || TemplateClassNames.Apollo}
											isMobile
											isEditor
										/>
									</aside>
								) : null}
							</div>
						)
					}
					<EditSectionColorsModal
						open={openEditNavItemColor}
						onClose={() => setOpenEditNavItemColor(false)}
						onFinish={handleNavItemColorOverrides}
						title={EContentColorOverrideNames.navItems}
					/>
					<HomePreviewModal open={previewMode === EPreviewTypes.Home || previewMode === EPreviewTypes.CustomPage} />
					<EditEventHeaderModal open={openEditModal} closeModal={() => setOpenEditModal(false)} />
				</div>
			</div>
		</>
	);
};
